import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Help() {
  const location = useLocation();
  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const [rules, setRules] = useState(null);
  const [rulesError, setRulesError] = useState("");
  const [loadingRules, setLoadingRules] = useState(true);

  const loadRules = useCallback(
    async (signal) => {
      setLoadingRules(true);
      setRulesError("");
      try {
        const response = await fetch(`${apiUrl}/api/v1/hdi/code-rules`, { signal });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.detail || `Could not load code rules (${response.status}).`);
        }
        setRules(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setRules(null);
          setRulesError(err.message || "Could not load accepted HDI code formats.");
        }
      } finally {
        if (!signal?.aborted) setLoadingRules(false);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadRules(controller.signal);
    return () => controller.abort();
  }, [loadRules]);

  useEffect(() => {
    if (!location.hash) return;
    window.requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView?.();
    });
  }, [location.hash]);

  return (
    <div className="help-page">
      <div className="help-heading-row">
        <div>
          <h1>Application Help</h1>
          <p>Guidance for inspection, registration, and module maintenance workflows.</p>
        </div>
        <Link to="/">Back to Home</Link>
      </div>

      <nav className="help-toc" aria-label="Help topics">
        <strong>Topics:</strong>
        <a href="#access">Access and environments</a>
        <a href="#hdi-inspection">HDI Inspection</a>
        <a href="#hdi-registration">HDI Registration</a>
        <a href="#module-registration">Module Registration</a>
        <a href="#edit-module">Edit Module</a>
        <a href="#troubleshooting">Troubleshooting</a>
      </nav>

      <section id="access" className="help-section">
        <h2>Access and environments</h2>
        <ul>
          <li>CERN login is required to use the application workflows.</li>
          <li>
            HDI Inspection needs authentication but does not require a database-loader (DCA)
            session.
          </li>
          <li>
            HDI Registration, Module Registration, and Edit Module require the PSI role and an
            active DCA session. The application prompts for DCA credentials when needed.
          </li>
          <li>
            Production is the default database. Use the development-database option only for test
            data; records in the two environments are separate.
          </li>
        </ul>
      </section>

      <section id="hdi-inspection" className="help-section">
        <h2>HDI Inspection</h2>
        <p>
          <Link to="/hdi-inspection">Open HDI Inspection</Link> and choose one acquisition method
          for the session.
        </p>
        <h3>Direct entry with a handscanner</h3>
        <ol>
          <li>
            Configure the keyboard-wedge scanner to send Enter/CR after each DataMatrix value.
          </li>
          <li>Place focus in the handscanner field and scan codes one after another.</li>
          <li>
            Accepted codes appear in scan order. Duplicates and invalid codes are not added and
            remain visible in Scan feedback.
          </li>
          <li>
            To paste a batch, put one complete code on each line and submit at most 100 at a time.
            Blank lines are ignored, and valid lines continue even if another line is invalid.
          </li>
        </ol>
        <h3>Image upload</h3>
        <p>
          Upload a JPEG, PNG, TIFF, or BMP up to 20 MB. The image scanner preserves panel layout and
          creates placeholders for unread, invalid, or duplicated segments. The same strict code
          rules are used for image and direct input.
        </p>
        <h3>Metadata, grading, and export</h3>
        <ul>
          <li>
            Set common inspection metadata, apply it to all items, and then adjust individual HDIs.
            A production date configured for an order is preserved unless the common date overrides
            it.
          </li>
          <li>
            Identification derived from a valid code is locked. Inspection metadata and inclusion in
            the JSON remain editable.
          </li>
          <li>
            The browser stores the current inspection session locally. Clear Session removes the
            acquired HDIs and resets metadata.
          </li>
          <li>
            Every enabled HDI must have all required fields before the JSON can be downloaded. The
            resulting file is the input for HDI Registration.
          </li>
        </ul>

        <div id="accepted-hdi-codes" className="help-subsection">
          <h3>Accepted HDI DataMatrix codes</h3>
          {loadingRules && <p>Loading the canonical code rules…</p>}
          {rulesError && (
            <div className="help-error" role="alert">
              <p>{rulesError}</p>
              <button type="button" onClick={() => loadRules()}>
                Retry
              </button>
            </div>
          )}
          {rules && (
            <>
              <p>
                <strong>Base format:</strong> {rules.base_format}. In the patterns below, PPP is the
                three-digit panel and BBB is the three-digit PCB number.
              </p>
              <div className="help-table-wrapper">
                <table className="help-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Suffix</th>
                      <th>Detector / PCB</th>
                      <th>Code pattern</th>
                      <th>HDI name pattern</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rules.families || []).map((family) => (
                      <tr key={`${family.order_number}-${family.suffix || "none"}`}>
                        <td>
                          <code>{family.order_number}</code>
                        </td>
                        <td>
                          <code>{family.suffix || "none"}</code>
                        </td>
                        <td>
                          {family.detector} / {family.pcb}
                        </td>
                        <td>
                          <code>{family.code_pattern}</code>
                        </td>
                        <td>
                          <code>{family.name_pattern}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                Unknown orders, missing or unexpected suffixes, incomplete numeric fields, changed
                letter case, internal whitespace, and extra trailing content are rejected. Add a new
                backend rule before inspecting a new production family.
              </p>
            </>
          )}
        </div>
      </section>

      <section id="hdi-registration" className="help-section">
        <h2>HDI Registration</h2>
        <ol>
          <li>Finish HDI Inspection and download the generated JSON.</li>
          <li>
            <Link to="/hdi-registration">Open HDI Registration</Link>, confirm the production or
            development environment, and select the JSON file.
          </li>
          <li>Review the parsed preview before choosing Upload JSON.</li>
          <li>
            Keep the page open while processing output streams. “Registration succeeded” is the
            terminal success state; stderr, a nonzero return code, or a missing final result is a
            failure.
          </li>
        </ol>
      </section>

      <section id="module-registration" className="help-section">
        <h2>Module Registration</h2>
        <ol>
          <li>
            <Link to="/module-registration">Open Module Registration</Link> and choose the
            subdetector and module type.
          </li>
          <li>Select an unattached HDI. TBPX B34 quad modules also require layer 3 or 4.</li>
          <li>
            Select compatible unattached bare modules at the HDI location. A 3D module needs two
            distinct bare modules in the displayed slot order.
          </li>
          <li>
            Enter the short name and required metadata. Gluing, wire-bonding, and coating dates and
            locations must be supplied as pairs.
          </li>
          <li>
            Review the computed name and generated JSON, resolve validation messages, and register
            only when the environment and component identities are correct.
          </li>
        </ol>
      </section>

      <section id="edit-module" className="help-section">
        <h2>Edit Module</h2>
        <ol>
          <li>
            <Link to="/edit-module">Open Edit Module</Link>, select the environment and module type,
            then search by name label or serial number.
          </li>
          <li>
            Identifiers are read-only. Editable part fields, attributes, and module-extension fields
            are supplied by the database schema for the selected module type.
          </li>
          <li>
            Save changes uploads only changed values. Reset Changes returns to the loaded values;
            Refresh from DB discards the local view and reloads current database state.
          </li>
          <li>Check the success message and upload details before leaving the page.</li>
        </ol>
      </section>

      <section id="troubleshooting" className="help-section">
        <h2>Troubleshooting</h2>
        <dl className="help-troubleshooting">
          <dt>A database page asks for DCA login</dt>
          <dd>Connect the DCA session for this browser. Reconnect if the session expired.</dd>
          <dt>No compatible HDI, bare module, or module is listed</dt>
          <dd>
            Confirm the environment, type, location, attachment state, search field, and search
            text. Production and development data are separate.
          </dd>
          <dt>A scanned code is rejected</dt>
          <dd>
            Compare it with the canonical table above. Do not edit a physical code to make it fit;
            request a rule update for a legitimate new family.
          </dd>
          <dt>A request times out or the final registration outcome is uncertain</dt>
          <dd>
            Preserve the displayed output and verify database state before retrying, to avoid a
            duplicate registration attempt.
          </dd>
          <dt>Validation prevents export, registration, or editing</dt>
          <dd>
            Read the field-specific message, supply paired fields together, and verify that selected
            components are compatible and unattached.
          </dd>
        </dl>
      </section>
    </div>
  );
}
